import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import CompanyID from '@/App/Company/Entity/CompanyID'
import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import {
    type BookingFormThemeType,
    type EmbedButtonSettingsType,
    type CompanyLogoFile,
    type CompanyCoverFile,
    CoverImagePositionEnum,
    type CompanyData,
} from '@/Shared/AutoGenerated/Main'
import InPersonHostDefinedMeetingLocation from '@/App/Company/Entity/InPersonHostDefinedMeetingLocation'

export default class Company {
    constructor(
        public readonly id: CompanyID,
        public readonly defaultAvailabilityId: AvailabilityID | null,
        public readonly slug: string,
        // public readonly slug: CompanySlug,
        public readonly displayName: string,
        public readonly logoImage: CompanyLogoFile | null,
        public readonly coverImage: CompanyCoverFile | null,
        public readonly defaultBookingFormTheme: BookingFormThemeType,
        public readonly embedButtonSettings: EmbedButtonSettingsType,
        public readonly inPersonHostDefinedMeetingLocations: InPersonHostDefinedMeetingLocation[],
    ) {
        ensureNoUndefinedProperties(this)
    }

    getLogoUrlWithFallback(): string {
        return this.logoImage?.url ?? '/static/images/company-placeholder.png'
    }

    shouldRenderCoverImage(): boolean {
        return (
            this.defaultBookingFormTheme.showCompanyCover && !!this.coverImage
        )
    }

    getCoverImage(): CompanyCoverFile {
        if (!this.coverImage) {
            throw new Error('No cover image available')
        }

        return this.coverImage
    }

    isCoverImageTopPositioned(): boolean {
        return (
            this.defaultBookingFormTheme.coverImagePosition ===
            CoverImagePositionEnum.Top
        )
    }

    isCoverImageCenterPositioned(): boolean {
        return (
            this.defaultBookingFormTheme.coverImagePosition ===
            CoverImagePositionEnum.Center
        )
    }

    isCoverImageBottomPositioned(): boolean {
        return (
            this.defaultBookingFormTheme.coverImagePosition ===
            CoverImagePositionEnum.Bottom
        )
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingleOrNull(entityData: CompanyData | null): Company | null {
        if (!entityData) {
            return null
        }

        return this.hydrateSingle(entityData)
    }

    static hydrateSingle(entityData: CompanyData): Company {
        return new Company(
            new CompanyID(entityData.id),
            entityData.defaultAvailabilityId
                ? new AvailabilityID(entityData.defaultAvailabilityId)
                : null,
            entityData.slug,
            entityData.displayName,
            entityData.logoImage,
            entityData.coverImage,
            entityData.defaultBookingFormTheme,
            entityData.embedButtonSettings,
            InPersonHostDefinedMeetingLocation.hydrateMultiple(
                entityData.inPersonHostDefinedMeetingLocations,
            ),
        )
    }

    static hydrateMultiple(entitiesData: CompanyData[]): Company[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
